import React, { useState, useEffect } from "react";
import Accordion from "../../Accordian/Accordian";
import InputComponent from "../commonInputBox/InputBox";
import style from "../Information/style.module.css";
import { getCountry } from "../../api/HttpApis";
import AiGeneratedBadge from "../aiAssist/AiGeneratedBadge";

const getHash = () => {
  try {
    return typeof window !== "undefined" ? window.location.hash : "";
  } catch {
    return "";
  }
};

const Technical = ({ soloProps, formData, handleChange, technicalData, setTechnicalData, aiFields }) => {
  const [countryList, setCountryList] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);

  const hash = getHash();

  // AI writes technical specs under `tech:<key>` keys in the shared aiFields set.
  const isAiSpec = (key) => Boolean(aiFields && aiFields.has && aiFields.has(`tech:${key}`));

  // Editable specs surfaced from the video's metadata (probed / AI-detected).
  const SPEC_FIELDS = [
    { key: "max_quality", label: "Max Quality", placeholder: "e.g. 4K (2160p)" },
    { key: "frame_rate", label: "Frame Rate", placeholder: "e.g. 24 fps" },
    { key: "aspect_ratio", label: "Aspect Ratio", placeholder: "e.g. 16:9" },
    { key: "video_codec", label: "Video Codec", placeholder: "e.g. H.264 / AVC" },
    { key: "audio_codec", label: "Audio Codec", placeholder: "e.g. AAC" },
    { key: "bitrate", label: "Bitrate", placeholder: "e.g. 12 Mbps" },
    { key: "hdr", label: "HDR Format", placeholder: "e.g. HDR10" },
  ];

  const isLive =
    formData?.["content-type"] === "Live Channel" ||
    formData?.["content-type"] === "Live Event";

  const isSeries =
    formData?.["content-type"] === "Series" ||
    hash.includes("/Addseries");

  useEffect(() => {
    getCountry()
      .then((data) => setCountryList(Array.isArray(data) ? data : []))
      .catch(() => setCountryList([]));
  }, []);

  const toggleCountry = (code) => {
    setSelectedCountries((prev) => {
      const updated = prev.includes(code)
        ? prev.filter((c) => c !== code)
        : [...prev, code];
      setTechnicalData((td) => ({ ...td, geo_restrictions: updated.join(", ") }));
      return updated;
    });
  };

  const handleTechnicalChange = (e) => {
    const { name, value } = e.target;
    setTechnicalData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleCheckbox = (key) => {
    setTechnicalData((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <Accordion
      title="Technical"
      icon={<img src={`${process.env.PUBLIC_URL}/technical.png`} alt="" width={18} height={18} />}
      openId="technical"
      {...soloProps}
    >
      <div className={style.technicalWrapper}>
        {!isLive && (
          <>
            <div className={style.technicalSpecHead}>
              <span className={style.technicalSpecTitle}>Video Specs</span>
              <span className={style.technicalLabelNote}>
                Probed from the source file — edit if anything looks off.
              </span>
            </div>
            <div className={style.technicalSpecGrid}>
              {SPEC_FIELDS.map(({ key, label, placeholder }) => (
                <div key={key} className={style.technicalGroup}>
                  <label className={style.technicalLabel}>
                    {label}
                    {isAiSpec(key) && <AiGeneratedBadge size="sm" />}
                  </label>
                  <input
                    name={key}
                    className={style.technicalSelect}
                    placeholder={placeholder}
                    value={technicalData?.[key] || ""}
                    onChange={handleTechnicalChange}
                  />
                </div>
              ))}
            </div>
          </>
        )}
        {!isLive && (
          <div className={style.technicalThreeRow}>
            <div className={style.technicalGroup}>
              <label className={style.technicalLabel}>DRM</label>
              <select
                name="drm"
                className={style.technicalSelect}
                value={technicalData?.drm || ""}
                onChange={handleTechnicalChange}
              >
                <option value="">Select</option>
                <option value="true">true</option>
                <option value="false">false</option>
              </select>
            </div>
          </div>
        )}

        <div className={style.technicalGroup}>
          <label className={style.technicalLabel}>
            Geo Restrictions{" "}
            <span className={style.technicalLabelNote}>Blocked country codes, comma-separated</span>
          </label>
          <select
            className={style.technicalSelect}
            value=""
            onChange={(e) => {
              if (e.target.value) toggleCountry(e.target.value);
            }}
          >
            <option value="">Select</option>
            {countryList
              .filter((c) => !selectedCountries.includes(c.Code))
              .map((c) => (
                <option key={c.Code} value={c.Code}>
                  {c.Name} ({c.Code})
                </option>
              ))}
          </select>
        </div>
      </div>

      {selectedCountries.length > 0 && (
        <div className={`${style.basicGenreRow} ${style.technicalCountryTagsRow}`}>
          {selectedCountries.map((code) => {
            const country = countryList.find((c) => c.Code === code);
            return (
              <button
                key={code}
                type="button"
                className={`${style.basicGenreTag} ${style.basicGenreTagActive}`}
                onClick={() => toggleCountry(code)}
              >
                {country ? `${country.Name} (${code})` : code} ×
              </button>
            );
          })}
        </div>
      )}

      <div className={style.technicalCheckboxRow}>
        <label className={style.technicalCheckboxLabel}>
          <input
            type="checkbox"
            checked={technicalData?.downloadable || false}
            onChange={() => toggleCheckbox("downloadable")}
            className={style.technicalCheckbox}
          />
          Downloadable
        </label>
        <label className={style.technicalCheckboxLabel}>
          <input
            type="checkbox"
            checked={technicalData?.hdrAvailable || false}
            onChange={() => toggleCheckbox("hdrAvailable")}
            className={style.technicalCheckbox}
          />
          HDR Available
        </label>
      </div>
    </Accordion>
  );
};

export default Technical;